import React, { useState } from "react";
import {
  uploadImage,
  saveFileProceedingForCandidate,
} from "../../services/files.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import { findTemplatesByPositionId } from "../../services/template.service.ts";
import {
  getProceedingByCandidate,
  createProceedingForCandidate as createProceedingService,
} from "../../services/proceedings.service.ts";

interface Document {
  id: number;
  name: string;
  obligatory: boolean;
  fields: any[];
}

interface DocumentFile {
  documentId: number;
  documentName: string;
  files: File[];
  fileIds: string[];
  fields: { field: string; value: string }[];
}

interface CandidateDocumentUploaderProps {
  candidateId: number | null;
  positionId: number | null;
  selectedDocuments: number[];
  applicationId: number;
  onFilesReady?: (uploadFiles: () => Promise<void>) => void;
  isSaving?: boolean;
}

const CandidateDocumentUploader: React.FC<CandidateDocumentUploaderProps> = ({
  candidateId,
  positionId,
  selectedDocuments,
  applicationId,
  onFilesReady,
  isSaving = false,
}) => {
  const [documentFiles, setDocumentFiles] = useState<DocumentFile[]>([]);
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const [proceedingId, setProceedingId] = useState<number | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<
    { documentId: number; fileId: string; fields: any[] }[]
  >([]);

  const mergeFields = React.useCallback(
    (
      docFields: any[] | undefined,
      prevFields: { field: string; value: string }[] | undefined
    ): { field: string; value: string }[] => {
      const normalizedDocFields = Array.isArray(docFields) ? docFields : [];
      const normalizedPrev = Array.isArray(prevFields) ? prevFields : [];

      return normalizedDocFields.map((field: string) => ({
        field,
        value: normalizedPrev.find((f) => f.field === field)?.value || "",
      }));
    },
    []
  );

  React.useEffect(() => {
    if (positionId && selectedDocuments.length > 0) {
      loadDocuments();
    } else {
      setDocumentFiles([]);
      setAllDocuments([]);
    }
  }, [positionId, selectedDocuments]);

  React.useEffect(() => {
    if (candidateId) {
      loadProceeding();
    }
  }, [candidateId]);

  const loadDocuments = async () => {
    if (!positionId) return;

    try {
      const response = await findTemplatesByPositionId(positionId);
      const templates = response.templates || [];
      const documentsList: Document[] = [];

      templates.forEach((template: any) => {
        template.documents.forEach((doc: Document) => {
          // Ocultar "Foto colaborador"
          if (doc.name === "Foto colaborador") {
            return;
          }
          if (!documentsList.find((d) => d.id === doc.id)) {
            documentsList.push(doc);
          }
        });
      });

      setAllDocuments(documentsList);

      const selectedDocs = documentsList.filter((doc) =>
        selectedDocuments.includes(doc.id)
      );

      // IMPORTANTE:
      // Cuando el usuario agrega/quita documentos seleccionados, NO debemos borrar
      // los archivos ya adjuntados en documentos previamente seleccionados.
      // Aquí hacemos merge con el estado anterior por documentId.
      setDocumentFiles((prev) => {
        const prevById = new Map<number, DocumentFile>();
        prev.forEach((df) => prevById.set(df.documentId, df));

        return selectedDocs.map((doc) => {
          const prevDoc = prevById.get(doc.id);
          return {
            documentId: doc.id,
            documentName: doc.name,
            files: prevDoc?.files || [],
            fileIds: prevDoc?.fileIds || [],
            fields: mergeFields(doc.fields, prevDoc?.fields),
          };
        });
      });
    } catch (error) {
      console.error("Error loading documents:", error);
    }
  };

  const loadProceeding = async () => {
    if (!candidateId) return;

    try {
      const proceeding = await getProceedingByCandidate(candidateId);
      if (proceeding?.proceeding?.id) {
        setProceedingId(proceeding.proceeding.id);
      }
    } catch (error) {
      console.error("Error loading proceeding:", error);
    }
  };

  const handleFileChange = (documentId: number, files: File[]) => {
    setDocumentFiles((prev) =>
      prev.map((docFile) =>
        docFile.documentId === documentId
          ? { ...docFile, files: files }
          : docFile
      )
    );
  };

  const handleRemoveFile = (documentId: number, fileIndex: number) => {
    setDocumentFiles((prev) =>
      prev.map((docFile) => {
        if (docFile.documentId === documentId) {
          const newFiles = [...docFile.files];
          newFiles.splice(fileIndex, 1);
          return { ...docFile, files: newFiles };
        }
        return docFile;
      })
    );
  };

  const handleFieldChange = (
    documentId: number,
    fieldName: string,
    value: string
  ) => {
    setDocumentFiles((prev) =>
      prev.map((docFile) => {
        if (docFile.documentId === documentId) {
          const existingField = docFile.fields.find(
            (f) => f.field === fieldName
          );
          const newFields = existingField
            ? docFile.fields.map((f) =>
                f.field === fieldName ? { ...f, value } : f
              )
            : [...docFile.fields, { field: fieldName, value }];
          return { ...docFile, fields: newFields };
        }
        return docFile;
      })
    );
  };

  const uploadAllFiles = React.useCallback(async () => {
    const filesToUpload = documentFiles.filter((df) => df.files.length > 0);
    
    if (filesToUpload.length === 0) {
      return;
    }

    for (const docFile of filesToUpload) {
      const document = allDocuments.find((d) => d.id === docFile.documentId);
      if (document?.fields && document.fields.length > 0) {
        const missingFields = document.fields.filter(
          (field) =>
            !docFile.fields.find(
              (f) => f.field === field && f.value && f.value.trim() !== ""
            )
        );
        if (missingFields.length > 0) {
          throw new Error(
            `Completa los campos requeridos para "${
              docFile.documentName
            }": ${missingFields.join(", ")}`
          );
        }
      }
    }

    let currentCandidateId = candidateId;
    if (!currentCandidateId) {
      try {
        const { getCandidateByApplication } = await import(
          "../../services/application.service.ts"
        );
        const candidateResponse = await getCandidateByApplication(
          applicationId
        );
        if (candidateResponse?.id) {
          currentCandidateId = candidateResponse.id;
        }
      } catch (error) {
        console.error("Error getting candidate:", error);
      }
    }

    let currentProceedingId = proceedingId;
    if (currentCandidateId && !currentProceedingId) {
      try {
        const proceeding = await getProceedingByCandidate(currentCandidateId);
        if (proceeding?.proceeding?.id) {
          currentProceedingId = proceeding.proceeding.id;
          setProceedingId(currentProceedingId);
        } else {
          const newProceeding = await createProceedingForCandidateLocal(
            currentCandidateId
          );
          currentProceedingId = newProceeding.id;
          setProceedingId(currentProceedingId);
        }
      } catch (error) {
        try {
          const newProceeding = await createProceedingForCandidateLocal(
            currentCandidateId
          );
          currentProceedingId = newProceeding.id;
          setProceedingId(currentProceedingId);
        } catch (createError) {
          throw new Error(
            "No se pudo crear el proceeding para asociar los documentos"
          );
        }
      }
    }

    if (!currentProceedingId) {
      throw new Error(
        "No se puede asociar los documentos. El candidato aún no ha sido creado."
      );
    }

    const uploadPromises = filesToUpload.flatMap((docFile) => {
      // Obtener el documento para mapear los campos correctamente
      const document = allDocuments.find(
        (d) => d.id === docFile.documentId
      );
      
      // Mapear campos al formato correcto (field, value)
      const fieldsArray = document?.fields
        ? document.fields.map((field: string) => ({
            field: field,
            value: docFile.fields.find((f) => f.field === field)?.value || "",
          }))
        : [];

      // Subir todos los archivos del documento
      return docFile.files.map(async (file) => {
        try {
          const fileResponse = await uploadImage(file);
          if (!fileResponse?.id) {
            throw new Error(
              `Error al subir el archivo de ${docFile.documentName}`
            );
          }

          const saveData = {
            proceedingId: currentProceedingId!,
            fileTypeId: docFile.documentId,
            fileId: fileResponse.id,
            fields: fieldsArray,
          };
          await saveFileProceedingForCandidate(saveData);

          return {
            documentId: docFile.documentId,
            fileId: fileResponse.id,
            fields: fieldsArray,
          };
        } catch (error: any) {
          throw new Error(
            `Error al subir ${docFile.documentName}: ${error.message}`
          );
        }
      });
    });

    const uploadedResults = await Promise.all(uploadPromises);
    
    // Agrupar resultados por documentId
    const uploadedByDocument = new Map<number, any[]>();
    uploadedResults.forEach((result) => {
      if (!uploadedByDocument.has(result.documentId)) {
        uploadedByDocument.set(result.documentId, []);
      }
      uploadedByDocument.get(result.documentId)!.push(result);
    });

    // Contar total de archivos subidos
    const totalFilesUploaded = uploadedResults.length;

    // Actualizar estados
    uploadedByDocument.forEach((results, documentId) => {
      const fileIds = results.map((r) => r.fileId);
      setDocumentFiles((prev) =>
        prev.map((df) =>
          df.documentId === documentId
            ? { ...df, files: [], fileIds: [...(df.fileIds || []), ...fileIds] }
            : df
        )
      );

      setUploadedFiles((prev) => {
        const filtered = prev.filter((f) => f.documentId !== documentId);
        return [...filtered, ...results];
      });
    });

    notification(
      "Éxito",
      `${totalFilesUploaded} archivo(s) subido(s) correctamente`,
      "success"
    );
  }, [
    documentFiles,
    allDocuments,
    candidateId,
    proceedingId,
    applicationId,
    positionId,
  ]);

  const uploadAllFilesRef = React.useRef(uploadAllFiles);
  React.useEffect(() => {
    uploadAllFilesRef.current = uploadAllFiles;
  }, [uploadAllFiles]);

  React.useEffect(() => {
    if (onFilesReady) {
      onFilesReady(async () => {
        await uploadAllFilesRef.current();
      });
    }
  }, [onFilesReady]);

  const createProceedingForCandidateLocal = async (
    candidateIdToUse?: number
  ) => {
    const idToUse = candidateIdToUse || candidateId;
    if (!idToUse || !positionId) {
      throw new Error("Faltan datos para crear el proceeding");
    }

    const response = await findTemplatesByPositionId(positionId);
    const templates = response.templates || [];
    if (templates.length === 0) {
      throw new Error("No hay plantillas para este puesto");
    }

    const template = templates[0].template;

    const proceeding = await createProceedingService(idToUse, template.id);
    return proceeding;
  };

  React.useEffect(() => {
    const associateUploadedFiles = async () => {
      if (candidateId && uploadedFiles.length > 0 && !proceedingId) {
        try {
          let currentProceedingId = proceedingId;
          if (!currentProceedingId) {
            try {
              const proceeding = await getProceedingByCandidate(candidateId);
              if (proceeding?.proceeding?.id) {
                currentProceedingId = proceeding.proceeding.id;
                setProceedingId(currentProceedingId);
              } else {
                const newProceeding = await createProceedingForCandidateLocal(
                  candidateId
                );
                currentProceedingId = newProceeding.id;
                setProceedingId(currentProceedingId);
              }
            } catch (error) {
              const newProceeding = await createProceedingForCandidateLocal(
                candidateId
              );
              currentProceedingId = newProceeding.id;
              setProceedingId(currentProceedingId);
            }
          }

            for (const uploadedFile of uploadedFiles) {
              try {
                // Obtener el documento para mapear los campos correctamente
                const document = allDocuments.find(
                  (d) => d.id === uploadedFile.documentId
                );
                const fieldsArray = document?.fields
                  ? document.fields.map((field: string) => {
                      const fieldValue = uploadedFile.fields.find(
                        (f) => f.field === field
                      );
                      return {
                        field: field,
                        value: fieldValue?.value || "",
                      };
                    })
                  : uploadedFile.fields;

                const saveData = {
                  proceedingId: currentProceedingId,
                  fileTypeId: uploadedFile.documentId,
                  fileId: uploadedFile.fileId,
                  fields: fieldsArray,
                };
                await saveFileProceedingForCandidate(saveData);
            } catch (error) {
              console.error(
                `Error asociando archivo ${uploadedFile.documentId}:`,
                error
              );
            }
          }
        } catch (error) {
          console.error("Error asociando archivos al candidate:", error);
        }
      }
    };

    associateUploadedFiles();
  }, [candidateId, uploadedFiles.length]);

  if (!positionId || selectedDocuments.length === 0) {
    return null;
  }

  if (documentFiles.length === 0) {
    return (
      <div className="alert alert-info mb-3">
        <i className="fas fa-info-circle me-2"></i>
        Selecciona documentos para subir archivos
      </div>
    );
  }

  return (
    <div className="card border-0 bg-light mt-3">
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="card-title mb-0 text-dark">
            <i className="fas fa-upload me-2"></i>
            Subir Documentos
          </h6>
          {!candidateId && (
            <span className="badge bg-warning text-dark">
              <i className="fa fa-info-circle me-1"></i>
              Los archivos se asociarán al guardar
            </span>
          )}
        </div>

        <div className="row g-3">
          {documentFiles.map((docFile) => {
            const document = allDocuments.find(
              (d) => d.id === docFile.documentId
            );
            const hasFields = document?.fields && document.fields.length > 0;
            const isUploaded = docFile.fileIds.length > 0;

            return (
              <div key={docFile.documentId} className="col-md-6 col-lg-4">
                <div
                  className="card border h-100"
                  style={{ transition: "all 0.2s ease" }}
                >
                  <div className="card-header bg-light d-flex justify-content-between align-items-center py-2">
                    <div className="d-flex align-items-center">
                      <i className="fa fa-file-alt me-2 text-primary"></i>
                      <small
                        className="fw-bold text-dark"
                        style={{ fontSize: "0.85rem" }}
                      >
                        {docFile.documentName}
                      </small>
                    </div>
                    {isUploaded && (
                      <span className="badge bg-success">
                        <i className="fa fa-check me-1"></i>
                      </span>
                    )}
                    {document?.obligatory && !isUploaded && (
                      <span className="badge bg-danger">Obligatorio</span>
                    )}
                  </div>
                  <div className="card-body p-3">
                    {/* Campos del documento */}
                    {hasFields && (
                      <div className="mb-2">
                        {document.fields.map((field: string) => (
                          <div key={field} className="mb-2">
                            <label className="form-label small mb-1">
                              {field}{" "}
                              {document.obligatory && (
                                <span className="text-danger">*</span>
                              )}
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={
                                docFile.fields.find((f) => f.field === field)
                                  ?.value || ""
                              }
                              onChange={(e) =>
                                handleFieldChange(
                                  docFile.documentId,
                                  field,
                                  e.target.value
                                )
                              }
                              disabled={isUploaded}
                              placeholder={`Ingresa ${field.toLowerCase()}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Selector de archivo */}
                    <div className="mb-2">
                      <label className="form-label small mb-1">Archivo(s)</label>
                      <input
                        type="file"
                        className="form-control form-control-sm"
                        accept=".pdf,.jpg,.jpeg,.png"
                        multiple
                        onChange={(e) => {
                          const selectedFiles = Array.from(e.target.files || []);
                          if (selectedFiles.length > 0) {
                            handleFileChange(
                              docFile.documentId,
                              [...docFile.files, ...selectedFiles]
                            );
                          }
                          // Limpiar el input para permitir seleccionar el mismo archivo de nuevo
                          e.target.value = "";
                        }}
                        disabled={isUploaded}
                      />
                      
                      {/* Lista de archivos seleccionados */}
                      {docFile.files.length > 0 && (
                        <div className="mt-2">
                          {docFile.files.map((file, index) => (
                            <div
                              key={index}
                              className="d-flex align-items-center justify-content-between bg-light p-2 mb-1 rounded"
                            >
                              <div className="d-flex align-items-center">
                                <i className="fa fa-file me-2 text-primary"></i>
                                <small className="text-dark">{file.name}</small>
                                <small className="text-muted ms-2">
                                  ({(file.size / 1024).toFixed(2)} KB)
                                </small>
                              </div>
                              {!isSaving && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-link text-danger p-0"
                                  onClick={() => handleRemoveFile(docFile.documentId, index)}
                                  title="Eliminar archivo"
                                >
                                  <i className="fa fa-times"></i>
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Estado del archivo */}
                    {isUploaded && (
                      <div className="alert alert-success mb-0 py-2">
                        <i className="fa fa-check-circle me-2"></i>
                        <small>
                          {docFile.fileIds.length} archivo(s) subido(s)
                        </small>
                      </div>
                    )}
                    {docFile.files.length > 0 && !isUploaded && (
                      <div className="alert alert-info mb-0 py-2">
                        <i className="fa fa-info-circle me-2"></i>
                        <small>
                          {docFile.files.length} archivo(s) listo(s) para subir
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Resumen */}
        {uploadedFiles.length > 0 && (
          <div className="alert alert-success mt-3 mb-0">
            <i className="fa fa-check-circle me-2"></i>
            <strong>{uploadedFiles.length}</strong> documento
            {uploadedFiles.length > 1 ? "s" : ""} subido
            {uploadedFiles.length > 1 ? "s" : ""} correctamente
            {!candidateId && (
              <span className="d-block mt-1 small">
                Se asociarán al candidato al guardar el formulario
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateDocumentUploader;
